import { useState } from "react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/utils";
import { X, Lock, Loader2, CheckCircle2, Eye, EyeOff } from "lucide-react";

export default function ChangePasswordModal({ open, onClose }) {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [show, setShow] = useState({ cur: false, nw: false });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  if (!open) return null;

  const close = () => {
    setForm({ current_password: "", new_password: "", confirm: "" });
    setErr("");
    setDone(false);
    onClose();
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.new_password.length < 6) {
      setErr("New password must be at least 6 characters");
      return;
    }
    if (form.new_password !== form.confirm) {
      setErr("New password and confirmation do not match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      setDone(true);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 fade-in" onClick={close}>
      <div className="bg-white w-full max-w-md rounded-lg shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="change-password-modal">
        <div className="flex items-center justify-between px-6 h-14 border-b border-stone-200">
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-rose-700" strokeWidth={1.8} />
            <h2 className="font-heading font-semibold">Change password</h2>
          </div>
          <button className="btn-ghost p-2 rounded" onClick={close} data-testid="change-password-close">
            <X className="w-5 h-5" strokeWidth={1.6} />
          </button>
        </div>

        {done ? (
          <div className="p-8 text-center space-y-4">
            <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto" strokeWidth={1.4} />
            <div>
              <div className="font-heading text-lg font-semibold">Password updated</div>
              <div className="text-sm text-stone-500 mt-1">Use your new password the next time you log in.</div>
            </div>
            <button className="btn btn-primary" onClick={close} data-testid="change-password-done-btn">Done</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4">
            <div>
              <label className="label">Current password</label>
              <div className="relative">
                <input
                  required
                  type={show.cur ? "text" : "password"}
                  className="input pr-10"
                  value={form.current_password}
                  onChange={(e) => setForm({ ...form, current_password: e.target.value })}
                  data-testid="cp-current"
                />
                <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-700 p-1" onClick={() => setShow({ ...show, cur: !show.cur })} tabIndex={-1}>
                  {show.cur ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="label">New password</label>
              <div className="relative">
                <input
                  required
                  minLength={6}
                  type={show.nw ? "text" : "password"}
                  className="input pr-10"
                  value={form.new_password}
                  onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                  data-testid="cp-new"
                />
                <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-700 p-1" onClick={() => setShow({ ...show, nw: !show.nw })} tabIndex={-1}>
                  {show.nw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <div className="text-[11px] text-stone-500 mt-1">At least 6 characters.</div>
            </div>
            <div>
              <label className="label">Confirm new password</label>
              <input
                required
                type={show.nw ? "text" : "password"}
                className="input"
                value={form.confirm}
                onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                data-testid="cp-confirm"
              />
            </div>

            {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn btn-outline" onClick={close} disabled={loading}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={loading} data-testid="cp-submit">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" strokeWidth={1.6} />}
                Update password
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
